import React, {useEffect} from 'react';
import useBraze from './useBraze';
import {runWhenBrazeReady} from './brazeQueue';
import {
  isRouteTrackable,
  onRouteResolved,
} from '@src/Tracking/utils/analyticsRouteState';

/**
 * If LayoutProvider never publishes a route state — it renders only once the
 * Apollo client is ready, so a failed/very slow client means it never mounts —
 * fall back to opening the session anyway. isRouteTrackable() then degrades to
 * URL-shape checking alone, which is still better than the pre-fix behaviour of
 * opening unconditionally. Deliberately generous: the subscription below
 * normally fires within a frame of the page rendering, so this only ever runs
 * in a degraded app.
 */
const ROUTE_RESOLVE_FALLBACK_MS = 5000;

const BrazeProvider = (props: {children?: React.ReactNode}) => {
  const {children} = props;
  const {initBraze, openBrazeSession} = useBraze();

  useEffect(() => {
    // initialize() is deliberately UNCONDITIONAL. It performs no network I/O
    // and creates no Braze profile (see @braze/web-sdk src/Core/initialize.js)
    // — it only prepares the SDK. Keeping it unconditional means
    // logCustomEvent always has a live SDK to talk to, so this guard cannot
    // cost a real user any event.
    initBraze();

    // openSession() is what tells Braze a user is present, and is therefore
    // what creates the anonymous profile. Gating it is what stops bot traffic
    // inflating the Braze user count: during the Aug 2026 sweep every scanner
    // hit opened a session and minted a profile.
    //
    // Safe because that traffic fires no Braze-routed events either — the
    // catalog routes only 14 engagement events to Braze, none of which a
    // scanner triggers — so with the session skipped nothing reaches Braze at
    // all for a non-page.
    //
    // Two deterministic conditions replace the old fixed 500ms delay:
    //   1. onRouteResolved — the route state is actually known, rather than
    //      hoping LayoutProvider rendered within an arbitrary window.
    //   2. runWhenBrazeReady — the SDK has finished initialising. Braze
    //      discards calls made before that (see brazeQueue.ts).
    //
    // Anonymous-by-default is preserved: on a real page the session still opens
    // without waiting on the consent banner.
    let cancelled = false;
    let opened = false;
    let unsubscribe: (() => void) | undefined;

    const attempt = () => {
      if (cancelled || opened) return;
      // Not a real page. Stay subscribed — if the visitor navigates from a 404
      // to a genuine route, LayoutProvider republishes and we open then.
      if (!isRouteTrackable()) return;

      opened = true;
      runWhenBrazeReady(() => {
        if (cancelled) return;
        openBrazeSession().catch(() => {});
      });
      unsubscribe?.();
    };

    // May invoke `attempt` synchronously if the route is already resolved, in
    // which case `unsubscribe` is still undefined — the `opened` flag below
    // covers that.
    unsubscribe = onRouteResolved(attempt);
    if (opened) unsubscribe();

    const fallback = setTimeout(attempt, ROUTE_RESOLVE_FALLBACK_MS);

    return () => {
      cancelled = true;
      clearTimeout(fallback);
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
};

export default React.memo(BrazeProvider);
